import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Search } from 'lucide-react';
import { Skill, Role, ProjectType, Project, Formation, Localized } from '../types';
import { localized } from '../utils';
import { BACKEND_URL } from '../config';

const CATEGORIES: Localized[] = [
  { fr: 'Compétence', en: 'Skill' },
  { fr: 'Soft skill', en: 'Soft skill' },
  { fr: 'Langage', en: 'Language' },
  { fr: 'Technologie', en: 'Technology' },
];

type Kind = 'skills' | 'roles' | 'types';
interface Row { id: number; name: Localized; category?: Localized; uses: number }

const input = 'h-11 border border-line bg-base text-ink placeholder:text-zinc-500 rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';
const small = 'h-9 border border-line bg-base text-ink rounded-lg px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';
const th = 'px-4 py-3 text-left text-xs font-medium text-zinc-500 whitespace-nowrap';
const ghost = 'h-9 px-3 rounded-lg border border-line text-[13px] font-medium text-ink hover:bg-surface transition-colors';

interface Props {
  auth: HeadersInit;
  skills: Skill[]; setSkills: React.Dispatch<React.SetStateAction<Skill[]>>;
  roles: Role[]; setRoles: React.Dispatch<React.SetStateAction<Role[]>>;
  projectTypes: ProjectType[]; setProjectTypes: React.Dispatch<React.SetStateAction<ProjectType[]>>;
  projects: Project[];
  formations: Formation[];
}

/** Admin "Références": skills, roles and project types in one filterable table. */
export default function ReferencesTab({ auth, skills, setSkills, roles, setRoles, projectTypes, setProjectTypes, projects, formations }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [kind, setKind] = useState<Kind>('skills');
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('');
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editId, setEditId] = useState<number | null>(null);
  const [edit, setEdit] = useState({ fr: '', en: '', cat: 0 });
  const [adding, setAdding] = useState(false);
  const [add, setAdd] = useState({ fr: '', en: '', cat: 0 });

  const rows: Row[] = useMemo(() => {
    const count: Record<number, number> = {};
    const bump = (id: number) => { count[id] = (count[id] || 0) + 1; };
    if (kind === 'skills') {
      projects.forEach(p => (p.skills || []).forEach(s => bump(s.id)));
      formations.forEach(f => (f.skills || []).filter(s => !s.from_project).forEach(s => bump(s.id)));
      return skills.map(s => ({ id: s.id, name: s.name, category: s.category, uses: count[s.id] || 0 }));
    }
    if (kind === 'roles') {
      projects.forEach(p => (p.roles || []).forEach(r => bump(r.id)));
      return roles.map(r => ({ id: r.id, name: r.name, uses: count[r.id] || 0 }));
    }
    projects.forEach(p => { if (p.type_id) bump(p.type_id); });
    return projectTypes.map(pt => ({ id: pt.id, name: pt.name, uses: count[pt.id] || 0 }));
  }, [kind, skills, roles, projectTypes, projects, formations]);

  const unusedCount = rows.filter(r => !r.uses).length;
  const q = query.trim().toLowerCase();
  const visible = rows
    .filter(r => (!q || Object.values(r.name).some(n => n.toLowerCase().includes(q))) && (!cat || r.category?.fr === cat) && (!unusedOnly || !r.uses))
    .sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang));
  const chosen = visible.filter(r => selected.has(r.id));

  const base = kind === 'skills' ? 'skills' : kind === 'roles' ? 'roles' : 'project-types';
  const setList = (fn: (rs: any[]) => any[]) => (kind === 'skills' ? setSkills(fn) : kind === 'roles' ? setRoles(fn) : setProjectTypes(fn));
  const jsonHeaders = { ...auth, 'Content-Type': 'application/json' };

  const switchKind = (k: Kind) => { setKind(k); setQuery(''); setCat(''); setUnusedOnly(false); setSelected(new Set()); setEditId(null); setAdding(false); };

  const create = async () => {
    if (!add.fr.trim() || !add.en.trim()) return;
    const body: Record<string, Localized> = { name: { fr: add.fr.trim(), en: add.en.trim() } };
    if (kind === 'skills') body.category = CATEGORIES[add.cat];
    const res = await fetch(`${BACKEND_URL}/api/${base}`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) }).catch(() => null);
    if (!res?.ok) return void toast.error(t('error_saving'));
    const created = await res.json();
    setList(prev => [...prev, created]);
    setAdd({ fr: '', en: '', cat: add.cat });
  };

  const update = async (id: number, body: Record<string, Localized>) => {
    const res = await fetch(`${BACKEND_URL}/api/${base}/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(body) }).catch(() => null);
    if (!res?.ok) { toast.error(t('error_saving')); return false; }
    const row = await res.json();
    setList(prev => prev.map(x => (x.id === id ? { ...x, ...row } : x)));
    return true;
  };

  const saveEdit = async () => {
    if (editId === null || !edit.fr.trim() || !edit.en.trim()) return;
    const body: Record<string, Localized> = { name: { fr: edit.fr.trim(), en: edit.en.trim() } };
    if (kind === 'skills') body.category = CATEGORIES[edit.cat];
    if (await update(editId, body)) setEditId(null);
  };

  const remove = async (ids: number[]) => {
    const results = await Promise.all(ids.map(id =>
      fetch(`${BACKEND_URL}/api/${base}/${id}`, { method: 'DELETE', headers: auth }).then(r => (r.ok ? id : null)).catch(() => null)
    ));
    const done = new Set(results.filter((id): id is number => id !== null));
    setList(prev => prev.filter(x => !done.has(x.id)));
    setSelected(prev => new Set([...prev].filter(id => !done.has(id))));
    if (done.size < ids.length) toast.error(t('error_saving')); else toast.success(t('saved'));
  };

  const removeSelected = () => {
    const used = chosen.filter(r => r.uses).length;
    const msg = used ? t('confirm_delete_used', { count: chosen.length, used }) : t('confirm_delete_selected', { count: chosen.length });
    if (window.confirm(msg as string)) remove(chosen.map(r => r.id));
  };

  const changeCategory = async (value: string) => {
    if (value === '') return;
    const target = CATEGORIES[+value];
    const results = await Promise.all(chosen.map(r => update(r.id, { category: target })));
    if (results.every(Boolean)) toast.success(t('saved'));
  };

  const startEdit = (r: Row) => {
    setEditId(r.id);
    setEdit({ fr: r.name.fr ?? '', en: r.name.en ?? '', cat: Math.max(0, CATEGORIES.findIndex(c => c.fr === r.category?.fr)) });
  };

  const toggle = (id: number) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allOn = visible.length > 0 && visible.every(r => selected.has(r.id));
  const cats = [t('skills_skills'), t('roles'), t('project_types')] as string[];
  const kinds: Kind[] = ['skills', 'roles', 'types'];
  const counts = [skills.length, roles.length, projectTypes.length];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('references') as string}>
        {kinds.map((k, i) => (
          <button key={k} type="button" aria-pressed={kind === k} onClick={() => switchKind(k)}
            className={`h-9 px-4 rounded-full text-[13px] font-medium border transition-colors ${kind === k ? 'bg-ink text-base border-ink' : 'border-line text-ink hover:bg-surface'}`}>
            {cats[i]} <span className="opacity-60">{counts[i]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block">
            <span className="sr-only">{t('search')}</span>
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={t('search') as string} className={`${input} w-64 pl-9`} />
          </label>
          {kind === 'skills' && (
            <select value={cat} onChange={e => setCat(e.target.value)} aria-label={t('category') as string} className={input}>
              <option value="">{t('all_categories')}</option>
              {CATEGORIES.map(c => <option key={c.fr} value={c.fr}>{localized(c, lang)}</option>)}
            </select>
          )}
          <label className="inline-flex items-center gap-2 min-h-[44px] text-sm text-ink cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={unusedOnly} onChange={e => setUnusedOnly(e.target.checked)} />
            {t('unused_only', { count: unusedCount })}
          </label>
        </div>
        <button type="button" onClick={() => setAdding(a => !a)} aria-expanded={adding}
          className="h-11 px-4 inline-flex items-center gap-1.5 rounded-full bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors">
          <Plus size={16} /> {t(kind === 'skills' ? 'add_skill' : kind === 'roles' ? 'add_role' : 'add_project_type')}
        </button>
      </div>

      {adding && (
        <div className="flex flex-col sm:flex-row gap-2 rounded-2xl border border-line p-4">
          {kind === 'skills' && (
            <select className={`${input} sm:w-44`} value={add.cat} onChange={e => setAdd({ ...add, cat: +e.target.value })} aria-label={t('category') as string}>
              {CATEGORIES.map((c, i) => <option key={i} value={i}>{localized(c, lang)}</option>)}
            </select>
          )}
          <input className={`${input} flex-1 min-w-0`} placeholder={t('name_fr') as string} aria-label={t('name_fr') as string} value={add.fr} onChange={e => setAdd({ ...add, fr: e.target.value })} />
          <input className={`${input} flex-1 min-w-0`} placeholder={t('name_en') as string} aria-label={t('name_en') as string} value={add.en} onChange={e => setAdd({ ...add, en: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); create(); } }} />
          <button type="button" onClick={create} className="h-11 px-5 rounded-lg bg-ink text-base text-sm font-semibold hover:bg-primary hover:text-white transition-colors">{t('add')}</button>
        </div>
      )}

      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface px-4 py-2.5 text-sm">
          <span>{t('selected_count', { count: chosen.length })}</span>
          <div className="flex flex-wrap gap-2">
            {kind === 'skills' && (
              <select className={small} value="" onChange={e => changeCategory(e.target.value)} aria-label={t('change_category') as string}>
                <option value="">{t('change_category')}</option>
                {CATEGORIES.map((c, i) => <option key={i} value={i}>{localized(c, lang)}</option>)}
              </select>
            )}
            <button type="button" onClick={removeSelected} className={`${ghost} !text-red-500 !border-red-500/40`}>{t('delete')}</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-line">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-surface">
              <th className={`${th} w-10`}><input type="checkbox" className="w-4 h-4 accent-primary" checked={allOn} aria-label="Select all"
                onChange={() => setSelected(allOn ? new Set() : new Set(visible.map(r => r.id)))} /></th>
              <th className={th}>{t('name_fr')}</th>
              <th className={th}>{t('name_en')}</th>
              {kind === 'skills' && <th className={th}>{t('category')}</th>}
              <th className={th}>{t('uses')}</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => {
              const editing = editId === r.id;
              return (
                <tr key={r.id} className="border-t border-line">
                  <td className="px-4 py-2"><input type="checkbox" className="w-4 h-4 accent-primary" checked={selected.has(r.id)} onChange={() => toggle(r.id)} aria-label={localized(r.name, lang)} /></td>
                  {editing ? (
                    <>
                      <td className="px-4 py-1.5"><input className={`${small} w-full`} value={edit.fr} aria-label={t('name_fr') as string} onChange={e => setEdit({ ...edit, fr: e.target.value })} /></td>
                      <td className="px-4 py-1.5"><input className={`${small} w-full`} value={edit.en} aria-label={t('name_en') as string} onChange={e => setEdit({ ...edit, en: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }} /></td>
                      {kind === 'skills' && (
                        <td className="px-4 py-1.5">
                          <select className={small} value={edit.cat} aria-label={t('category') as string} onChange={e => setEdit({ ...edit, cat: +e.target.value })}>
                            {CATEGORIES.map((c, i) => <option key={i} value={i}>{localized(c, lang)}</option>)}
                          </select>
                        </td>
                      )}
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-2.5 font-medium text-ink">{r.name.fr}</td>
                      <td className="px-4 py-2.5 text-muted">{r.name.en}</td>
                      {kind === 'skills' && (
                        <td className="px-4 py-2.5"><span className="inline-flex h-6 items-center px-2.5 rounded-full bg-surface text-xs font-medium text-primary-light">{r.category ? localized(r.category, lang) : ''}</span></td>
                      )}
                    </>
                  )}
                  <td className={`px-4 py-2.5 font-mono ${r.uses ? 'text-muted' : 'text-red-500 font-semibold'}`}>{r.uses}</td>
                  <td className="px-4 py-1.5 text-right whitespace-nowrap">
                    {editing ? (
                      <>
                        <button type="button" onClick={() => setEditId(null)} className={ghost}>{t('cancel')}</button>{' '}
                        <button type="button" onClick={saveEdit} className={`${ghost} !bg-ink !text-base !border-ink`}>OK</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => startEdit(r)} className={ghost}>{t('edit')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">{t(kind === 'skills' ? 'no_skills' : kind === 'roles' ? 'no_roles' : 'no_project_types')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
